import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import SeatMapMarkerEl from '../components/SeatMapMarker';

const STATUS_CFG = {
  free:     { bg: '#d1fae5', border: '#34d399', text: '#065f46' },
  reserved: { bg: '#fef3c7', border: '#fbbf24', text: '#92400e' },
  booked:   { bg: '#dbeafe', border: '#60a5fa', text: '#1e3a8a' },
  blocked:  { bg: '#fee2e2', border: '#f87171', text: '#991b1b' },
};

const STATUS_LABELS = { free: 'Frei', reserved: 'Reserviert', booked: 'Gebucht', blocked: 'Gesperrt' };

export default function SeatMap({ eventId: eventIdProp, anonymous = false } = {}) {
  const params = useParams();
  const eventId = eventIdProp || params.eventId;
  const { user } = useAuth();
  const [seats, setSeats]           = useState([]);
  const [markers, setMarkers]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null);
  const [tapped, setTapped]         = useState(null); // occupied seat info popup
  const [message, setMessage]       = useState('');
  const [error, setError]           = useState('');
  const [myReservation, setMyReservation] = useState(null);
  const dismissTimer = useRef(null);

  const loadSeats = useCallback(() => {
    api.get(`/api/events/${eventId}/seats`).then((r) => {
      setSeats(r.data);
      setMyReservation(r.data.find((s) => s.booked_by_username === user?.username) || null);
    }).finally(() => setLoading(false));
  }, [eventId, user]);

  useEffect(() => {
    loadSeats();
    api.get(`/api/events/${eventId}/markers`).then((r) => setMarkers(r.data)).catch(() => {});
  }, [loadSeats, eventId]);

  const reserve = async () => {
    if (!selected) return;
    setError(''); setMessage('');
    try {
      await api.post(`/api/seats/${selected.id}/reserve`);
      setMessage(`✅ Sitzplatz ${selected.label} erfolgreich gebucht!`);
      setSelected(null); loadSeats();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
  };

  const release = async () => {
    if (!myReservation) return;
    try {
      await api.delete(`/api/seats/${myReservation.id}/reserve`);
      setMessage('Sitzplatz freigegeben.'); setMyReservation(null); loadSeats();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
  };

  const handleSeatTap = (seat) => {
    if (anonymous) return;
    const isMine = seat.booked_by_username === user?.username;
    const canSelect = seat.status === 'free' && !myReservation && user;

    if (canSelect) {
      setTapped(null);
      setSelected((prev) => prev?.id === seat.id ? null : seat);
      return;
    }

    if (seat.status !== 'free' && seat.status !== 'blocked' && seat.booked_by_nickname) {
      setSelected(null);
      setTapped((prev) => prev?.id === seat.id ? null : seat);
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
      dismissTimer.current = setTimeout(() => setTapped(null), 4000);
    }
  };

  const maxX = Math.max(...seats.map((s) => s.pos_x), ...markers.map((m) => m.pos_x + m.width), 10) + 70;
  const maxY = Math.max(...seats.map((s) => s.pos_y), ...markers.map((m) => m.pos_y + m.height), 10) + 70;

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-2 border-ocean-400 border-t-transparent"/></div>;

  return (
    <div>
      {!eventIdProp && <h1 className="page-title">Sitzplan</h1>}

      {message && <div className="alert-success mb-4">{message}</div>}
      {error && <div className="alert-error mb-4">{error}</div>}

      {anonymous && (
        <div className="card border-l-4 border-l-slate-300 mb-4 bg-slate-50 text-slate-500 text-sm py-3">
          Melde dich für dieses Event an, um Sitzplätze zu sehen und zu buchen.
        </div>
      )}

      {!anonymous && myReservation && (
        <div className="card border-l-4 border-l-reef-400 mb-4 flex justify-between items-center">
          <p className="text-sm">Dein Platz: <span className="font-bold text-reef-600">💺 {myReservation.label}</span></p>
          <Button variant="ghost" size="sm" onClick={release}>Freigeben</Button>
        </div>
      )}

      {/* Legende */}
      <div className="flex gap-4 flex-wrap mb-4">
        {Object.entries(STATUS_LABELS).map(([k, v]) => {
          const c = STATUS_CFG[k];
          return (
            <div key={k} className="flex items-center gap-1.5 text-xs font-medium">
              <div className="w-4 h-4 rounded" style={{ background: c.bg, border: `1.5px solid ${c.border}` }}/>
              {v}
            </div>
          );
        })}
        <div className="flex items-center gap-1.5 text-xs font-medium">
          <div className="w-4 h-4 rounded" style={{ background: '#0EA5E9', border: '1.5px solid #0284C7' }}/>
          Dein Platz
        </div>
      </div>

      <p className="text-xs text-slate-400 mb-2">Scrollen zum Navigieren, Tippen zum Auswählen oder Anzeigen.</p>
      <div className="card overflow-auto mb-4 p-2 sm:p-5" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y' }}>
        <div className="relative" style={{ width: maxX, height: maxY, minWidth: 300 }}>
          {markers.map((m) => (
            <SeatMapMarkerEl key={`m-${m.id}`} marker={m} readonly />
          ))}

          {seats.map((seat) => {
            const isMine = seat.booked_by_username === user?.username;
            const isSelected = selected?.id === seat.id;
            const isTapped = tapped?.id === seat.id;
            const canSelect = seat.status === 'free' && !myReservation && user;
            const cfg = isMine ? { bg: '#0EA5E9', border: '#0284C7', text: '#fff' } : STATUS_CFG[seat.status] || STATUS_CFG.free;
            const isOccupied = seat.status !== 'free' && seat.status !== 'blocked' && seat.booked_by_nickname;

            return (
              <div key={seat.id} style={{ position: 'absolute', left: seat.pos_x, top: seat.pos_y }}>
                <div
                  onClick={() => handleSeatTap(seat)}
                  title={anonymous ? seat.label : `${seat.label}${seat.booked_by_username ? ` — ${seat.booked_by_username}` : ''}`}
                  style={{
                    width: 44, height: 44,
                    background: isSelected || isTapped ? cfg.border : cfg.bg,
                    border: `2px solid ${cfg.border}`,
                    borderRadius: 8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.7rem', fontWeight: 700,
                    color: isSelected || isTapped ? '#fff' : cfg.text,
                    cursor: (canSelect || isOccupied) ? 'pointer' : 'default',
                    transform: isSelected || isTapped ? 'scale(1.15)' : 'scale(1)',
                    transition: 'all 0.12s',
                    boxShadow: isMine ? '0 0 0 3px #0EA5E966' : isSelected || isTapped ? '0 0 0 3px #0EA5E944' : 'none',
                  }}>
                  {seat.label}
                </div>

                {/* Tap-Popup für belegte Plätze */}
                {!anonymous && isTapped && seat.booked_by_nickname && (
                  <div style={{
                    position: 'absolute',
                    bottom: 50,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 50,
                    whiteSpace: 'nowrap',
                  }}>
                    <div style={{
                      background: '#1e293b',
                      color: '#f1f5f9',
                      borderRadius: 8,
                      padding: '6px 12px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}>
                      <span>💺</span>
                      <span>{seat.booked_by_nickname}</span>
                    </div>
                    {/* Arrow */}
                    <div style={{
                      width: 0, height: 0,
                      borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent',
                      borderTop: '6px solid #1e293b',
                      margin: '0 auto',
                    }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {selected && (
        <div className="card border border-ocean-200 bg-ocean-50">
          <p className="text-sm mb-3">Sitzplatz <span className="font-bold text-ocean-700">{selected.label}</span> buchen?</p>
          <div className="flex gap-2">
            <Button onClick={reserve}>✓ Bestätigen</Button>
            <Button variant="ghost" onClick={() => setSelected(null)}>Abbrechen</Button>
          </div>
        </div>
      )}

      {seats.length === 0 && <div className="card text-center py-8 text-slate-400">Noch keine Sitzplätze angelegt.</div>}
    </div>
  );
}
